import type {
  AddressSearchResponse,
  AreaPlacesResponse,
  AreaResponse,
  AreaSearchResponse,
  AnalysisDetailResponse,
  AnalysisSummaryResponse,
  CheckoutResponse,
  CheckoutStatusResponse,
  ComparisonResponse,
  ApiErrorBody,
  ProductCode,
  UnlockOption,
  StreetViewStatus,
} from '../types/api';
import { getLang, tx } from '../i18n';
import { analysisKey, reportToken, sessionKey } from './session';

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

const SESSION_HEADER = 'X-ScanHome-Session';
const ANALYSIS_KEY_HEADER = 'X-ScanHome-Analysis-Key';
const ENTITLEMENT_HEADER = 'X-ScanHome-Entitlement';

/**
 * An API failure, carrying the server's machine readable code.
 *
 * Callers branch on {@link code}, never on the message, which is written for a tenant and may be
 * reworded at any time.
 */
export class ApiError extends Error {
  readonly status: number;

  readonly code: string;

  readonly meta: Record<string, unknown>;

  readonly details: Record<string, string[]> | undefined;

  constructor(
    status: number,
    code: string,
    message: string,
    meta: Record<string, unknown> = {},
    details?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.meta = meta;
    this.details = details;
  }

  /** Paid, but Stripe has not confirmed it yet. Worth polling on. */
  get isPaymentPending(): boolean {
    return this.code === 'PAYMENT_NOT_CONFIRMED';
  }

  /** Nothing covers this address; the caller should be offered the unlock options. */
  get isLocked(): boolean {
    return this.code === 'DETAIL_NOT_UNLOCKED';
  }

  get isCompareLimit(): boolean {
    return this.code === 'COMPARE_LIMIT_REACHED';
  }

  get isBundleExhausted(): boolean {
    return this.code === 'BUNDLE_EXHAUSTED';
  }
}

/*
 * The API's error codes in the reader's language; the server writes its own messages in English.
 *
 * Errors are the one place a string is looked up by key rather than written in place: the code is
 * what the server sends, and there is no English sentence at the call site to write alternatives
 * beside. A code with no entry for the language falls back to the server's English, like every
 * other string.
 */
const ERRORS: Record<string, Record<string, string>> = {
  tr: {
    ADDRESS_NOT_FOUND: 'Bu adres bulunamadı. Listeden bir adres seçin.',
    ANALYSIS_ACCESS_DENIED: 'Bu rapor bağlantısı geçerli değil.',
    ANALYSIS_NOT_FOUND: 'Bu rapor bulunamadı.',
    ANALYSIS_TIMEOUT: 'Veri kaynakları zamanında yanıt vermedi. Lütfen tekrar deneyin.',
    BUNDLE_EXHAUSTED: 'Paketinizdeki tüm konut hakları kullanıldı.',
    COMPARE_LIMIT_REACHED: 'Ücretsiz karşılaştırma hakkınız doldu.',
    DETAIL_NOT_UNLOCKED: 'Bu raporun ayrıntıları henüz açılmadı.',
    INVALID_POSTCODE: 'Bu geçerli bir Birleşik Krallık posta kodu gibi görünmüyor.',
    OUT_OF_SERVICE_AREA: 'Şimdilik yalnızca İngiltere kapsanıyor.',
    PAYMENT_NOT_CONFIRMED: 'Ödeme henüz onaylanmadı.',
    PAYMENT_UNAVAILABLE: 'Ödeme şu anda alınamıyor. Lütfen daha sonra tekrar deneyin.',
    RATE_LIMITED: 'Çok fazla istek gönderildi. Lütfen bir dakika sonra tekrar deneyin.',
    REPORT_NOT_READY: 'Rapor henüz hazır değil.',
    TOKEN_EXPIRED: 'Bu rapor bağlantısının süresi doldu.',
    UPSTREAM_UNAVAILABLE: 'Bir kamu veri kaynağına şu anda ulaşılamıyor. Lütfen tekrar deneyin.',
    INTERNAL_ERROR: 'Bizim tarafımızda bir sorun oluştu.',
  },
  pl: {
    ADDRESS_NOT_FOUND: 'Nie znaleziono tego adresu. Wybierz adres z listy.',
    ANALYSIS_ACCESS_DENIED: 'Ten link do raportu jest nieprawidłowy.',
    ANALYSIS_NOT_FOUND: 'Nie znaleziono tego raportu.',
    ANALYSIS_TIMEOUT: 'Źródła danych nie odpowiedziały na czas. Spróbuj ponownie.',
    BUNDLE_EXHAUSTED: 'Wykorzystano wszystkie mieszkania z pakietu.',
    COMPARE_LIMIT_REACHED: 'Limit bezpłatnych porównań został wyczerpany.',
    DETAIL_NOT_UNLOCKED: 'Szczegóły tego raportu nie zostały jeszcze odblokowane.',
    INVALID_POSTCODE: 'To nie wygląda na brytyjski kod pocztowy.',
    OUT_OF_SERVICE_AREA: 'Na razie obsługujemy tylko Anglię.',
    PAYMENT_NOT_CONFIRMED: 'Płatność nie została jeszcze potwierdzona.',
    PAYMENT_UNAVAILABLE: 'Obecnie nie można przyjąć płatności. Spróbuj później.',
    RATE_LIMITED: 'Zbyt wiele zapytań. Spróbuj ponownie za minutę.',
    REPORT_NOT_READY: 'Raport nie jest jeszcze gotowy.',
    TOKEN_EXPIRED: 'Ten link do raportu wygasł.',
    UPSTREAM_UNAVAILABLE: 'Publiczne źródło danych jest chwilowo niedostępne. Spróbuj ponownie.',
    INTERNAL_ERROR: 'Coś poszło nie tak po naszej stronie.',
  },
  pt: {
    ADDRESS_NOT_FOUND: 'Esta morada não foi encontrada. Escolha uma morada da lista.',
    ANALYSIS_ACCESS_DENIED: 'Este link do relatório não é válido.',
    ANALYSIS_NOT_FOUND: 'Este relatório não foi encontrado.',
    ANALYSIS_TIMEOUT: 'As fontes de dados não responderam a tempo. Tente novamente.',
    BUNDLE_EXHAUSTED: 'Todas as casas do seu pacote foram utilizadas.',
    COMPARE_LIMIT_REACHED: 'Esgotou as comparações gratuitas.',
    DETAIL_NOT_UNLOCKED: 'Os detalhes deste relatório ainda não foram desbloqueados.',
    INVALID_POSTCODE: 'Isto não parece um código postal britânico.',
    OUT_OF_SERVICE_AREA: 'Por agora só cobrimos Inglaterra.',
    PAYMENT_NOT_CONFIRMED: 'O pagamento ainda não foi confirmado.',
    PAYMENT_UNAVAILABLE: 'De momento não é possível aceitar pagamentos. Tente mais tarde.',
    RATE_LIMITED: 'Demasiados pedidos. Tente novamente dentro de um minuto.',
    REPORT_NOT_READY: 'O relatório ainda não está pronto.',
    TOKEN_EXPIRED: 'Este link do relatório expirou.',
    UPSTREAM_UNAVAILABLE: 'Uma fonte de dados públicos está indisponível. Tente novamente.',
    INTERNAL_ERROR: 'Algo correu mal do nosso lado.',
  },
  es: {
    ADDRESS_NOT_FOUND: 'No se ha encontrado esta dirección. Elige una de la lista.',
    ANALYSIS_ACCESS_DENIED: 'Este enlace del informe no es válido.',
    ANALYSIS_NOT_FOUND: 'No se ha encontrado este informe.',
    ANALYSIS_TIMEOUT: 'Las fuentes de datos no respondieron a tiempo. Inténtalo de nuevo.',
    BUNDLE_EXHAUSTED: 'Has usado todas las viviendas de tu paquete.',
    COMPARE_LIMIT_REACHED: 'Has agotado las comparaciones gratuitas.',
    DETAIL_NOT_UNLOCKED: 'Los detalles de este informe aún no se han desbloqueado.',
    INVALID_POSTCODE: 'Esto no parece un código postal británico.',
    OUT_OF_SERVICE_AREA: 'Por ahora solo cubrimos Inglaterra.',
    PAYMENT_NOT_CONFIRMED: 'El pago aún no se ha confirmado.',
    PAYMENT_UNAVAILABLE: 'Ahora mismo no se pueden aceptar pagos. Inténtalo más tarde.',
    RATE_LIMITED: 'Demasiadas peticiones. Inténtalo dentro de un minuto.',
    REPORT_NOT_READY: 'El informe todavía no está listo.',
    TOKEN_EXPIRED: 'Este enlace del informe ha caducado.',
    UPSTREAM_UNAVAILABLE: 'Una fuente de datos públicos no está disponible. Inténtalo de nuevo.',
    INTERNAL_ERROR: 'Algo ha ido mal por nuestra parte.',
  },
};

function localisedError(code: string, english: string): string {
  return ERRORS[getLang()]?.[code] ?? english;
}

/** Longer than the backend's 25 second collection ceiling, so the server normally answers first. */
const REQUEST_TIMEOUT_MS = 45_000;

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // A request must never leave the visitor on a spinner: abort after the timeout, and still honour
  // any signal the caller passed in.
  const controller = new AbortController();
  let timedOut = false;
  const timer = window.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, REQUEST_TIMEOUT_MS);
  const callerSignal = init?.signal;
  if (callerSignal) {
    if (callerSignal.aborted) {
      controller.abort();
    } else {
      callerSignal.addEventListener('abort', () => controller.abort(), { once: true });
    }
  }

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      ...init,
      signal: controller.signal,
      headers: {
        Accept: 'application/json',
        'Accept-Language': getLang(),
        [SESSION_HEADER]: sessionKey(),
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
        ...init?.headers,
      },
    });
  } catch (cause) {
    if (timedOut) {
      throw new ApiError(
        0,
        'TIMEOUT',
        tx(
          'This is taking longer than it should. Please try again in a moment.',
          'Bu işlem beklenenden uzun sürüyor. Lütfen birazdan tekrar deneyin.',
    'To trwa dłużej, niż powinno. Spróbuj ponownie za chwilę.',
    'Isto está a demorar mais do que devia. Tente novamente daqui a pouco.',
    'Esto tarda más de lo que debería. Inténtalo de nuevo en un momento.',
        ),
      );
    }
    if (callerSignal?.aborted) {
      throw cause;
    }
    throw new ApiError(
      0,
      'NETWORK_ERROR',
      tx('We could not reach ScanHome. Check your connection.', "ScanHome'e ulaşılamadı. Bağlantınızı kontrol edin.",
    'Nie udało się połączyć z ScanHome. Sprawdź połączenie.',
    'Não foi possível contactar o ScanHome. Verifique a sua ligação.',
    'No se ha podido contactar con ScanHome. Comprueba tu conexión.'),
    );
  } finally {
    window.clearTimeout(timer);
  }

  if (!response.ok) {
    let body: ApiErrorBody | null = null;
    try {
      body = (await response.json()) as ApiErrorBody;
    } catch {
      body = null;
    }
    const code = body?.error ?? 'UNEXPECTED_ERROR';
    throw new ApiError(
      response.status,
      code,
      localisedError(code, body?.message ?? tx('Something went wrong. Please try again.', 'Bir şeyler ters gitti. Lütfen tekrar deneyin.',
    'Coś poszło nie tak. Spróbuj ponownie.',
    'Algo correu mal. Tente novamente.',
    'Algo ha ido mal. Inténtalo de nuevo.')),
      body?.meta ?? {},
      body?.details,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

export const api = {
  /** The addresses inside a postcode. */
  addresses(postcode: string, signal?: AbortSignal): Promise<AddressSearchResponse> {
    return request<AddressSearchResponse>(
      `/api/v1/addresses?postcode=${encodeURIComponent(postcode)}`,
      signal ? { signal } : undefined,
    );
  },

  /**
   * Assesses a chosen address and returns the free summary.
   *
   * <p>{@code refresh} asks the server to ignore the stored report and put the question to every
   * live source again. It is off by default, which is what keeps a second visit to the same
   * address instant and keeps the public registers we read from unbothered.
   */
  createAnalysis(input: {
    postcode: string;
    uprn?: string | null;
    addressLine?: string | null;
    refresh?: boolean;
  }): Promise<AnalysisSummaryResponse> {
    return request<AnalysisSummaryResponse>('/api/v1/analyses', {
      method: 'POST',
      body: JSON.stringify({
        postcode: input.postcode,
        uprn: input.uprn ?? null,
        addressLine: input.addressLine ?? null,
        refresh: input.refresh ?? false,
      }),
    });
  },

  /** The free summary for an analysis this browser has already run. */
  summary(analysisId: string, signal?: AbortSignal): Promise<AnalysisSummaryResponse> {
    const key = analysisKey(analysisId);
    return request<AnalysisSummaryResponse>(`/api/v1/analyses/${analysisId}/summary`, {
      ...(signal ? { signal } : {}),
      headers: key ? { [ANALYSIS_KEY_HEADER]: key } : {},
    });
  },

  /** The paid detail. Throws DETAIL_NOT_UNLOCKED when nothing covers this address. */
  detail(analysisId: string, signal?: AbortSignal): Promise<AnalysisDetailResponse> {
    const token = reportToken();
    return request<AnalysisDetailResponse>(`/api/v1/analyses/${analysisId}/detail`, {
      ...(signal ? { signal } : {}),
      headers: token ? { [ENTITLEMENT_HEADER]: token } : {},
    });
  },

  /** The places that can be asked about: crawled outward codes, and the named areas. */
  areaPlaces(signal?: AbortSignal): Promise<AreaPlacesResponse> {
    return request<AreaPlacesResponse>('/api/v1/areas/places', signal ? { signal } : undefined);
  },

  /**
   * Several outward codes under one set of criteria. The criteria live in the query string, so a
   * search is a link that can be sent to somebody.
   */
  areas(
    outcodes: string[],
    filters: {
      minBand?: string | null;
      phase?: string | null;
      schoolMetres?: number | null;
      goodOnly?: boolean;
      stationMetres?: number | null;
      busMetres?: number | null;
    },
    signal?: AbortSignal,
  ): Promise<AreaResponse> {
    const params = new URLSearchParams({ outcodes: outcodes.join(',') });
    if (filters.minBand) {
      params.set('minBand', filters.minBand);
    }
    if (filters.phase) {
      params.set('phase', filters.phase);
    }
    if (filters.schoolMetres) {
      params.set('schoolMetres', String(filters.schoolMetres));
    }
    if (filters.goodOnly) {
      params.set('goodOnly', 'true');
    }
    if (filters.stationMetres) {
      params.set('stationMetres', String(filters.stationMetres));
    }
    if (filters.busMetres) {
      params.set('busMetres', String(filters.busMetres));
    }
    return request<AreaResponse>(
      `/api/v1/areas?${params.toString()}`,
      signal ? { signal } : undefined,
    );
  },

  /**
   * One outward code, drawn from what the crawler has already read.
   *
   * <p>Aggregate district data, so it is free and cacheable and carries no report key. An outcode
   * nobody has crawled comes back with `crawled: false` rather than an error.
   */
  area(
    outcode: string,
    filters: { minBand?: string | null; phase?: string | null; schoolMetres?: number; goodOnly?: boolean },
    signal?: AbortSignal,
  ): Promise<AreaResponse> {
    const params = new URLSearchParams();
    if (filters.minBand) {
      params.set('minBand', filters.minBand);
    }
    if (filters.phase) {
      params.set('phase', filters.phase);
    }
    if (filters.schoolMetres) {
      params.set('schoolMetres', String(filters.schoolMetres));
    }
    if (filters.goodOnly) {
      params.set('goodOnly', 'true');
    }
    const query = params.toString();
    return request<AreaResponse>(
      `/api/v1/areas/${encodeURIComponent(outcode)}${query ? `?${query}` : ''}`,
      signal ? { signal } : undefined,
    );
  },

  /**
   * A sentence, answered.
   *
   * <p>What it was taken to mean comes back with the result, because the reader has to be able to
   * see that "north london" became forty outward codes and that only some of them have been read.
   */
  areaSearch(q: string, signal?: AbortSignal): Promise<AreaSearchResponse> {
    return request<AreaSearchResponse>(
      `/api/v1/areas/search?q=${encodeURIComponent(q)}`,
      signal ? { signal } : undefined,
    );
  },

  /** The product catalogue. */
  pricing(): Promise<UnlockOption[]> {
    return request<UnlockOption[]>('/api/v1/pricing');
  },

  /** Opens a Stripe Checkout session. */
  createCheckout(input: {
    productCode: ProductCode;
    propertyAddressId?: string | null;
    email: string;
  }): Promise<CheckoutResponse> {
    return request<CheckoutResponse>('/api/v1/checkouts', {
      method: 'POST',
      body: JSON.stringify({
        productCode: input.productCode,
        propertyAddressId: input.propertyAddressId ?? null,
        email: input.email,
      }),
    });
  },

  /** Polled after returning from Stripe. */
  checkoutStatus(sessionId: string): Promise<CheckoutStatusResponse> {
    return request<CheckoutStatusResponse>(`/api/v1/checkouts/${encodeURIComponent(sessionId)}`);
  },

  /** The comparison table. */
  comparison(signal?: AbortSignal): Promise<ComparisonResponse> {
    return request<ComparisonResponse>(
      '/api/v1/shortlists/current',
      signal ? { signal } : undefined,
    );
  },

  /** Adds a home to the comparison. Throws COMPARE_LIMIT_REACHED at the free limit. */
  addToComparison(propertyAddressId: string, analysisId: string | null): Promise<ComparisonResponse> {
    return request<ComparisonResponse>('/api/v1/shortlists/items', {
      method: 'POST',
      body: JSON.stringify({ propertyAddressId, analysisId }),
    });
  },

  /** Removes a home from the comparison. */
  removeFromComparison(propertyAddressId: string): Promise<ComparisonResponse> {
    return request<ComparisonResponse>(
      `/api/v1/shortlists/items/${encodeURIComponent(propertyAddressId)}`,
      { method: 'DELETE' },
    );
  },

  /** Whether the report can show a street photo. Free: no image is fetched. */
  streetView(analysisId: string, signal?: AbortSignal): Promise<StreetViewStatus> {
    const key = analysisKey(analysisId);
    return request<StreetViewStatus>(`/api/v1/analyses/${analysisId}/street-view`, {
      ...(signal ? { signal } : {}),
      headers: key ? { [ANALYSIS_KEY_HEADER]: key } : {},
    });
  },

  /** The photo URL for an <img>, which cannot send headers, so the key goes in the query. */
  streetViewImageUrl(analysisId: string, imagePath: string): string {
    const key = analysisKey(analysisId);
    return `${BASE_URL}${imagePath}${key ? `?key=${encodeURIComponent(key)}` : ''}`;
  },

  /** Absolute URL of the PDF, with the report token attached for direct download. */
  pdfUrl(analysisId: string): string {
    const token = reportToken();
    const params = new URLSearchParams({ lang: getLang() });
    if (token) {
      params.set('token', token);
    }
    const query = `?${params.toString()}`;
    return `${BASE_URL}/api/v1/reports/${encodeURIComponent(analysisId)}/pdf${query}`;
  },
};
